import '@/styles/mcp-api.css';
import {Button, Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle} from '@tryghost/shade';
import {LucideIcon, formatNumber} from '@tryghost/shade/utils';
import {Select} from '@/components/ui/Select';
import {testApiTokenConnection} from '@/api/tokens';
import {useApiTokenActivity, useApiTokens, useCreateApiToken, useRevokeApiToken} from '@/hooks/useApiTokens';
import {useCreateSiteAdminKey, useRevealSiteAdminKey, useRevokeSiteAdminKey, useRotateSiteAdminKey, useSiteCredentials} from '@/hooks/useSiteCredentials';
import {useEffect, useMemo, useState} from 'react';
import {useSites} from '@/hooks/useSites';
import type {ApiToken, McpConnectionTestResult, SiteId} from '@/types/api';

function formatDate(date: string | null) {
    return date ? new Date(date).toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : 'Never';
}

function getTokenStatus(token: ApiToken) {
    return token.revoked_at ? 'revoked' : token.expires_at && new Date(token.expires_at) <= new Date() ? 'expired' : 'active';
}

function getMcpUrl() {
    return import.meta.env.VITE_MCP_URL?.trim() || null;
}

function isMcpCompatibleAdminUrl(adminUrl: string) {
    const configuredSuffix = import.meta.env.VITE_MCP_ADMIN_HOST_SUFFIX?.trim().toLowerCase();
    if (!configuredSuffix) {
        return true;
    }
    try {
        const url = new URL(adminUrl);
        const suffix = configuredSuffix.startsWith('.') ? configuredSuffix : `.${configuredSuffix}`;
        return url.protocol === 'https:' && url.hostname.toLowerCase().endsWith(suffix);
    } catch {
        return false;
    }
}

function codexConfiguration(url: string, token: string) {
    return `[mcp_servers.tribetale]\nurl = ${JSON.stringify(url)}\nhttp_headers = { Authorization = ${JSON.stringify(`Bearer ${token}`)} }`;
}

const contentEndpoints = [
    {label: 'Posts', path: 'posts/'},
    {label: 'Pages', path: 'pages/'},
    {label: 'Authors', path: 'authors/'},
    {label: 'Tags', path: 'tags/'},
    {label: 'Newsletters', path: 'newsletters/'}
];

function contentRequestUrl(baseUrl: string, path: string, key: string) {
    const url = new URL(path, baseUrl);
    url.searchParams.set('key', key);
    return url.toString();
}

function CopyButton({value, label = 'Copy', className = '', children, ariaLabel}: {value: string; label?: string; className?: string; children?: React.ReactNode; ariaLabel?: string}) {
    const [status, setStatus] = useState<'idle' | 'copied' | 'error'>('idle');
    useEffect(() => {
        setStatus('idle');
    }, [value]);
    useEffect(() => {
        if (status !== 'copied') {
            return;
        }
        const timeout = window.setTimeout(() => setStatus('idle'), 2500);
        return () => window.clearTimeout(timeout);
    }, [status]);
    return <span className={`mcp-copy-action${children ? ' mcp-copy-action-wide' : ''}`}>
        <Button aria-label={ariaLabel} className={`mcp-button ${className}`} variant="outline" onClick={async () => {
            try {
                await navigator.clipboard.writeText(value);
                setStatus('copied');
            } catch {
                setStatus('error');
            }
        }}>
            {children}
            <span className="mcp-copy-feedback">
                {status === 'copied' ? <LucideIcon.Check size={14}/> : <LucideIcon.Copy size={14}/>}
                <span aria-live="polite">{status === 'copied' ? 'Copied' : label}</span>
            </span>
        </Button>
        {status === 'error' && <small className="mcp-error" role="alert">Couldn’t copy. Select and copy the text instead.</small>}
    </span>;
}

function SecretContent({plaintext, tokenId, admin = false}: {plaintext: string; tokenId?: string | null; admin?: boolean}) {
    const mcpUrl = getMcpUrl();
    const configuration = !admin && mcpUrl ? codexConfiguration(mcpUrl, plaintext) : null;
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState<McpConnectionTestResult | null>(null);
    return <>
        <DialogHeader className="studio-create-dialog-heading">
            <DialogTitle>{admin ? 'Admin API key' : 'Save your token'}</DialogTitle>
            <DialogDescription>{admin ? 'Keep this key on your server. It gives write access to this publication.' : 'Copy it now. You won’t be able to see this token again.'}</DialogDescription>
        </DialogHeader>
        <code className="mcp-secret ph-no-capture">{plaintext}</code>
        {!admin && configuration && <div className="mcp-codex-configuration">
            <strong>Connect Codex</strong>
            <p>Add this block to <code>~/.codex/config.toml</code>. Keep any existing server blocks in the file.</p>
            <pre className="ph-no-capture"><code>{configuration}</code></pre>
            <CopyButton label="Copy Codex configuration" value={configuration}/>
            {tokenId && <Button className="mcp-button" disabled={isTesting} variant="outline" onClick={async () => {
                setIsTesting(true);
                try {
                    setTestResult(await testApiTokenConnection(tokenId, plaintext));
                } catch {
                    setTestResult({ok: false, message: 'Could not run the connection test. Try again.'});
                } finally {
                    setIsTesting(false);
                }
            }}>{isTesting ? 'Testing connection…' : 'Test connection'}</Button>}
            {testResult && <p role="status">{testResult.ok ? `Connected. ${formatNumber(testResult.siteCount ?? 0)} accessible publications found.` : testResult.message}</p>}
        </div>}
        {!admin && !configuration && <p className="mcp-secret-help">The MCP server URL is unavailable. Save this token securely, then ask your administrator to configure the endpoint before connecting Codex.</p>}
        <div className="mcp-dialog-actions">
            <DialogClose asChild><Button className="mcp-button" variant="outline">Done</Button></DialogClose>
            <CopyButton className="mcp-primary" label={admin ? 'Copy key' : 'Copy token'} value={plaintext}/>
        </div>
    </>;
}

function McpConnection({onCreateToken}: {onCreateToken: () => void}) {
    const url = getMcpUrl();
    return <section aria-labelledby="mcp-connection-title" className="mcp-section mcp-connect-section">
        <div className="mcp-section-label">
            <span className="mcp-section-kicker">For Codex</span>
            <h2 id="mcp-connection-title">Connect Codex with MCP</h2>
            <p>Work on your publication from Codex.</p>
            <p className="mcp-capabilities"><strong>Read:</strong> list publications, posts, and newsletters; get a post.<br/><strong>Write:</strong> create or update drafts; publish or schedule. Publishing sends email immediately, and scheduling sends it later. Both require confirmation. QA member tools work only on staging sites.</p>
        </div>
        <div className="mcp-connection">
            <div className="mcp-endpoint">
                <div className="mcp-endpoint-label"><LucideIcon.Link size={15}/><span>Server URL</span></div>
                {url ? <div className="mcp-endpoint-value"><code>{url}</code><CopyButton label="Copy URL" value={url}/></div> : <p className="mcp-error" role="status">MCP connection is unavailable. Ask your administrator to configure this workspace’s MCP server URL.</p>}
            </div>
            <ol className="mcp-steps">
                <li><div><strong>Create a token</strong><p>Choose which publications Codex can access.</p></div><Button className="mcp-text-button" variant="ghost" onClick={onCreateToken}>Add token <LucideIcon.ArrowUpRight size={15}/></Button></li>
                <li><div><strong>Add the configuration to Codex</strong><p>Copy the configuration shown with your new token into <code>~/.codex/config.toml</code>.</p></div></li>
            </ol>
            <details className="mcp-config">
                <summary>Connection details <LucideIcon.ChevronDown size={14}/></summary>
                <div><p>For clients that ask for an HTTP header, use:</p><code>Authorization: Bearer &lt;your-token&gt;</code><p>Use an automation token from this page, beginning with <code>ttp_</code>. Your site API keys belong in direct API integrations.</p></div>
            </details>
        </div>
    </section>;
}

function SiteCredentialsSection({onSecret}: {onSecret: (secret: string) => void}) {
    const {data: sites, isLoading, isError} = useSites();
    const ownedSites = useMemo(() => sites?.filter(site => site.access_type === 'owner' && site.status === 'active') ?? [], [sites]);
    const [selectedSiteId, setSelectedSiteId] = useState<SiteId>();
    const siteId = ownedSites.find(site => site.id === selectedSiteId)?.id ?? ownedSites[0]?.id;
    const credentials = useSiteCredentials(siteId);
    const reveal = useRevealSiteAdminKey();
    const rotate = useRotateSiteAdminKey();
    const revoke = useRevokeSiteAdminKey();
    const create = useCreateSiteAdminKey();
    const [actionError, setActionError] = useState<string | null>(null);
    const isPending = reveal.isLoading || rotate.isLoading || revoke.isLoading || create.isLoading;

    useEffect(() => {
        setActionError(null);
    }, [siteId]);

    const handleAction = async (action: 'reveal' | 'rotate' | 'revoke' | 'create') => {
        if (!siteId) {
            return;
        }
        if (action === 'rotate' && !window.confirm('Rotate this Admin API key? The old key will stop working immediately.')) {
            return;
        }
        if (action === 'revoke' && !window.confirm('Revoke this Admin API key? Write access will stop immediately.')) {
            return;
        }
        setActionError(null);
        try {
            if (action === 'revoke') {
                await revoke.mutateAsync(siteId);
            } else {
                const mutation = {reveal, rotate, create}[action];
                onSecret((await mutation.mutateAsync(siteId)).key);
            }
        } catch (error) {
            setActionError(error instanceof Error ? error.message : 'Could not update this key. Please try again.');
        }
    };

    return <section aria-labelledby="site-credentials-title" className="mcp-section">
        <div className="mcp-section-label">
            <span className="mcp-section-kicker">For custom integrations</span>
            <h2 id="site-credentials-title">Site API credentials</h2>
            <p>Connect a website, app, or service directly to a publication.</p>
        </div>
        <div className="mcp-site-credentials">
            {isLoading && <p className="mcp-note" role="status">Loading publications…</p>}
            {isError && <p className="mcp-error" role="alert">Could not load newsletters. Refresh the page to try again.</p>}
            {!isLoading && !isError && ownedSites.length === 0 && <p className="mcp-note">API credentials will appear here when you own an active publication.</p>}
            {ownedSites.length > 0 && <label className="mcp-site-select">Publication<Select ariaLabel="Publication" disabled={isPending} options={ownedSites.map(site => ({value: site.id, label: site.name}))} value={siteId} width="75%" onChange={value => setSelectedSiteId(value as SiteId)} /></label>}
            {siteId && credentials.isLoading && <p className="mcp-note" role="status">Loading API credentials…</p>}
            {siteId && credentials.isError && <p className="mcp-error" role="alert">{credentials.error instanceof Error ? credentials.error.message : 'Could not load API credentials.'}</p>}
            {siteId && credentials.data && <div className="mcp-keys">
                <article className="mcp-key">
                    <div className="mcp-key-heading"><h3>Content API</h3><span>Read only</span></div>
                    <p>Read published content from a website or app.</p>
                    <div className="mcp-key-value"><code>{credentials.data.contentKey}</code><CopyButton label="Copy key" value={credentials.data.contentKey}/></div>
                    <div className="mcp-api-examples mcp-content-endpoints">
                        {contentEndpoints.map((endpoint) => {
                            const url = contentRequestUrl(credentials.data.contentApiUrl, endpoint.path, credentials.data.contentKey);
                            return <CopyButton key={endpoint.path} ariaLabel={`Copy ${endpoint.label} endpoint URL`} className="mcp-content-endpoint" label="Copy URL" value={url}>
                                <span>{endpoint.label}</span>
                                <code>{url}</code>
                            </CopyButton>;
                        })}
                    </div>
                </article>
                <article className="mcp-key">
                    <div className="mcp-key-heading"><h3>Admin API</h3><span>Write access</span></div>
                    <p>Create, edit, and publish content. Keep this key private and on your server.</p>
                    <div className="mcp-key-value"><code>{credentials.data.adminKey.masked || 'No active key'}</code>
                        {credentials.data.adminKey.status === 'active' ? <Button className="mcp-button" disabled={isPending} variant="outline" onClick={() => void handleAction('reveal')}><LucideIcon.Eye size={14}/>Reveal</Button> : <Button className="mcp-button" disabled={isPending} variant="outline" onClick={() => void handleAction('create')}>Create Admin key</Button>}
                    </div>
                    <p className="mcp-key-instructions">Admin requests use a JWT signed with this key in <code>Authorization: TribeTale &lt;token&gt;</code>. Do not send the Admin key itself as a Bearer token or query parameter.</p>
                    <div className="mcp-api-examples">
                        <h4>Example endpoint</h4>
                        <div className="mcp-api-example"><span>Posts</span><code>{new URL('posts/', credentials.data.apiUrl).toString()}</code><CopyButton label="Copy endpoint" value={new URL('posts/', credentials.data.apiUrl).toString()}/></div>
                    </div>
                    {credentials.data.adminKey.status === 'active' && <div className="mcp-key-actions"><Button className="mcp-text-button" disabled={isPending} variant="ghost" onClick={() => void handleAction('rotate')}>Rotate key</Button><Button className="mcp-text-button mcp-danger" disabled={isPending} variant="ghost" onClick={() => void handleAction('revoke')}>Revoke key</Button></div>}
                </article>
            </div>}
            {actionError && <p className="mcp-error" role="alert">{actionError}</p>}
        </div>
    </section>;
}

function CreateTokenForm({onTokenCreated, onPendingChange}: {onTokenCreated: (token: string, id: string) => void; onPendingChange: (pending: boolean) => void}) {
    const {data: sites, isLoading, isError} = useSites();
    const createToken = useCreateApiToken();
    const [name, setName] = useState('');
    const [clientLabel, setClientLabel] = useState('Codex');
    const [scopeMode, setScopeMode] = useState<'all' | 'selected'>('all');
    const [selectedSites, setSelectedSites] = useState<SiteId[]>([]);
    const [expiresAt, setExpiresAt] = useState('');
    const [error, setError] = useState<string | null>(null);
    const ownedSites = sites?.filter(site => site.access_type === 'owner' && site.status === 'active') ?? [];
    const unsupportedSites = ownedSites.filter(site => !isMcpCompatibleAdminUrl(site.adminUrl));
    const canSubmit = name.trim().length > 0 && (scopeMode === 'all' || selectedSites.length > 0);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!canSubmit || createToken.isLoading) {
            return;
        }
        setError(null);
        onPendingChange(true);
        try {
            const result = await createToken.mutateAsync({name: name.trim(), clientLabel: clientLabel.trim() || undefined, scopes: scopeMode === 'all' ? null : selectedSites, expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null});
            onTokenCreated(result.token, result.apiToken.id);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Could not create a token. Please try again.');
        } finally {
            onPendingChange(false);
        }
    };

    return <>
        <DialogHeader className="studio-create-dialog-heading"><DialogTitle>Add an automation token</DialogTitle><DialogDescription>Give each AI tool or integration its own token.</DialogDescription></DialogHeader>
        <form className="mcp-token-form" onSubmit={handleSubmit}>
            <fieldset disabled={createToken.isLoading}>
                <label className="mcp-field">Token name<input autoComplete="off" placeholder="e.g. My writing assistant" value={name} autoFocus onChange={event => setName(event.target.value)}/></label>
                <label className="mcp-field">Client label<input autoComplete="off" maxLength={100} value={clientLabel} onChange={event => setClientLabel(event.target.value)}/><small>Identify the client in token activity.</small></label>
                {unsupportedSites.length > 0 && <p className="mcp-error" role="status">MCP tools cannot open these publications because their Admin URL is outside the configured host suffix: {unsupportedSites.map(site => site.name).join(', ')}.</p>}
                <fieldset className="mcp-scope"><legend>Publication access</legend>
                    <label><input checked={scopeMode === 'all'} name="scope" type="radio" onChange={() => setScopeMode('all')}/><span>All owned publications<small>Includes publications you create in the future.</small></span></label>
                    <label><input checked={scopeMode === 'selected'} name="scope" type="radio" onChange={() => setScopeMode('selected')}/><span>Selected publications<small>Choose exactly what this token can access.</small></span></label>
                    {scopeMode === 'selected' && <div className="mcp-site-picker">
                        {isLoading && <p role="status">Loading publications…</p>}
                        {isError && <p className="mcp-error" role="alert">Could not load newsletters. Try again later.</p>}
                        {!isLoading && !isError && ownedSites.length === 0 && <p>No active publications available.</p>}
                        {ownedSites.map(site => <label key={site.id}><input checked={selectedSites.includes(site.id)} type="checkbox" onChange={() => setSelectedSites(current => (current.includes(site.id) ? current.filter(id => id !== site.id) : [...current, site.id]))}/>{site.name}</label>)}
                    </div>}
                </fieldset>
                <label className="mcp-field">Expiration <span>Optional</span><input type="date" value={expiresAt} onChange={event => setExpiresAt(event.target.value)}/><small>Leave blank to keep the token active until you revoke it.</small></label>
            </fieldset>
            {error && <p className="mcp-error" role="alert">{error}</p>}
            <div className="mcp-dialog-actions"><DialogClose asChild><Button className="mcp-button" disabled={createToken.isLoading} type="button" variant="outline">Cancel</Button></DialogClose><Button className="mcp-button mcp-primary" disabled={!canSubmit || createToken.isLoading} type="submit">{createToken.isLoading ? 'Creating…' : 'Create token'}</Button></div>
        </form>
    </>;
}

function TokenRow({token}: {token: ApiToken}) {
    const revokeToken = useRevokeApiToken();
    const [error, setError] = useState<string | null>(null);
    const [showActivity, setShowActivity] = useState(false);
    const activity = useApiTokenActivity(token.id, showActivity);
    const status = getTokenStatus(token);
    const scopeCount = token.scopes?.length ?? 0;
    return <li className="mcp-token-row">
        <div className="mcp-token-name"><strong>{token.name}</strong><code>{token.token_prefix}••••••••</code>{token.client_label && <small>{token.client_label}</small>}</div>
        <div className="mcp-token-detail"><span>{token.scopesInvalid ? 'Invalid scope' : scopeCount ? `${formatNumber(scopeCount)} selected publication${scopeCount === 1 ? '' : 's'}` : 'All owned publications'}</span><small>{token.last_used_at ? `Last used ${formatDate(token.last_used_at)}` : 'Not used yet'}</small></div>
        <div className="mcp-token-state"><span className={`mcp-status is-${status}`}>{status}</span><small>{token.expires_at ? `Expires ${formatDate(token.expires_at)}` : 'No expiration'}</small></div>
        {status === 'active' && <Button aria-label={`Revoke ${token.name}`} className="mcp-text-button mcp-danger" disabled={revokeToken.isLoading} variant="ghost" onClick={async () => {
            if (!window.confirm(`Revoke "${token.name}"? Any tools using it will lose access. This cannot be undone.`)) {
                return;
            }
            setError(null);
            try {
                await revokeToken.mutateAsync(token.id);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Could not revoke the token. Please try again.');
            }
        }}>{revokeToken.isLoading ? 'Revoking…' : 'Revoke'}</Button>}
        <div className="mcp-token-activity"><Button aria-expanded={showActivity} className="mcp-text-button" variant="ghost" onClick={() => setShowActivity(current => !current)}>{showActivity ? 'Hide activity' : 'View activity'}</Button>
            {showActivity && <div>
                {activity.isLoading && <p role="status">Loading activity…</p>}
                {activity.isError && <p role="alert">Could not load activity.</p>}
                {activity.data?.pages.flatMap(page => page.items).length === 0 && <p>No activity yet.</p>}
                {activity.data?.pages.map(page => <ul key={page.items[0]?.id || 'empty'}>{page.items.map(item => <li key={item.id}>{formatDate(item.created_at)} · {item.tool || item.action} · {item.outcome || 'Event'}{item.error_code ? ` (${item.error_code})` : ''}</li>)}</ul>)}
                {activity.hasNextPage && <Button className="mcp-text-button" disabled={activity.isFetchingNextPage} variant="ghost" onClick={() => void activity.fetchNextPage()}>{activity.isFetchingNextPage ? 'Loading…' : 'Load more'}</Button>}
            </div>}
        </div>
        {error && <p className="mcp-error" role="alert">{error}</p>}
    </li>;
}

export function ApiTokensPage() {
    const {data: tokenData, isLoading, isError, error, refetch} = useApiTokens();
    const [dialog, setDialog] = useState<'create' | 'token' | 'admin' | null>(null);
    const [secret, setSecret] = useState('');
    const [secretTokenId, setSecretTokenId] = useState<string | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const tokens = tokenData ?? [];
    const isDialogOpen = dialog !== null;
    useEffect(() => {
        if (isDialogOpen) {
            document.body.classList.add('studio-create-dialog-open');
        }
        return () => document.body.classList.remove('studio-create-dialog-open');
    }, [isDialogOpen]);
    const closeDialog = () => {
        if (!isCreating) {
            setDialog(null);
            setSecret('');
            setSecretTokenId(null);
        }
    };

    return <div className="mcp-api-page">
        <header className="mcp-page-heading"><p className="mcp-section-kicker">Your tools, connected</p><h1>API &amp; MCP</h1><p>Bring your publication into the tools you already use.</p></header>
        <SiteCredentialsSection onSecret={(key) => {
            setSecret(key);
            setDialog('admin');
        }}/>
        <McpConnection onCreateToken={() => setDialog('create')}/>
        <section aria-labelledby="automation-tokens-title" className="mcp-section">
            <div className="mcp-section-label"><span className="mcp-section-kicker">Access &amp; permissions</span><h2 id="automation-tokens-title">Automation tokens</h2><p>Manage access for your AI tools and automations. Revoke a token to disconnect a tool.</p></div>
            <div className="mcp-token-library">
                <div className="mcp-library-heading"><span>{isLoading || isError ? 'Your tokens' : `${formatNumber(tokens.length)} token${tokens.length === 1 ? '' : 's'}`}</span><Button className="mcp-button mcp-primary" onClick={() => setDialog('create')}><LucideIcon.Plus size={15}/>Add token</Button></div>
                {isLoading && <p className="mcp-empty" role="status">Loading tokens…</p>}
                {isError && <div className="mcp-empty" role="alert"><strong>Couldn’t load your tokens</strong><p>{error instanceof Error ? error.message : 'Please try again.'}</p><Button className="mcp-button" variant="outline" onClick={() => void refetch()}>Try again</Button></div>}
                {!isLoading && !isError && tokens.length === 0 && <div className="mcp-empty"><LucideIcon.KeyRound aria-hidden="true" size={21}/><h3>No tokens yet</h3><p>Create your first token to connect an AI tool or automation.</p></div>}
                {!isLoading && !isError && tokens.length > 0 && <ul aria-label="Automation tokens" className="mcp-token-list">{tokens.map(token => <TokenRow key={token.id} token={token}/>)}</ul>}
                <p className="mcp-library-note">Tokens are shown only once when created. Give each tool its own token.</p>
            </div>
        </section>
        <Dialog open={isDialogOpen} onOpenChange={open => !open && closeDialog()}>
            <DialogContent className="studio-active studio-create-dialog mcp-dialog" onEscapeKeyDown={event => isCreating && event.preventDefault()} onInteractOutside={event => (isCreating || dialog === 'token') && event.preventDefault()}>
                <DialogClose asChild><Button aria-label="Close dialog" className="studio-create-dialog-close" disabled={isCreating} variant="ghost"><LucideIcon.X size={18}/></Button></DialogClose>
                {dialog === 'create' ? <CreateTokenForm onPendingChange={setIsCreating} onTokenCreated={(token, id) => {
                    setSecret(token);
                    setSecretTokenId(id);
                    setDialog('token');
                }}/> : <SecretContent admin={dialog === 'admin'} plaintext={secret} tokenId={secretTokenId}/>}
            </DialogContent>
        </Dialog>
    </div>;
}
